import { Box } from '@mui/material'
import type { Feature, GeoJSON as GeoJSONType } from 'geojson'
import type L from 'leaflet'
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useMap } from 'react-leaflet'
import AppContext from '@/context/appContext'
import { useMapDimensions } from '@/hooks/useMapDimensions'
import { Map } from '@/components/Map/Map'
import QUOverlay from '@/components/DataCollectionMap/QUOverlay'
import EligibilityLayers from '@/components/map/EligibilityLayers'
import { useStratificationContext } from './StratificationContext'
import { buildStratumPopupContent } from './stratumPopup'

// Hiding only restyles the layer, so Leaflet's close-popup-on-remove never fires for it.
const HiddenSamplingAreaPopupCloser: FC<{ hiddenQUIds: ReadonlySet<string> }> = ({
  hiddenQUIds,
}) => {
  const map = useMap()

  useEffect(() => {
    map.eachLayer(layer => {
      const quId = (layer as L.Layer & { feature?: Feature }).feature?.properties
        ?.quantificationUnitId
      if (hiddenQUIds.has(quId) && layer.isPopupOpen()) layer.closePopup()
    })
  }, [map, hiddenQUIds])

  return null
}

export const StratificationMap: FC = () => {
  const {
    samplingAreas,
    isLoadingSamplingAreas,
    clusters,
    quantificationUnits,
    eligibilityCheckAreas,
    hiddenSamplingAreaQUIds,
  } = useStratificationContext()
  const { currentFarm } = useContext(AppContext)
  const { mapWidth, mapHeight } = useMapDimensions()

  // react-leaflet's GeoJSON reads `data` only on mount. React Query's structural sharing keeps
  // `samplingAreas` referentially stable until its content (geometry included) changes, so a new
  // reference is exactly when the layer must be remounted to draw it.
  const [drawnSamplingAreas, setDrawnSamplingAreas] = useState(samplingAreas)
  const [samplingAreasLayerKey, setSamplingAreasLayerKey] = useState(0)
  if (samplingAreas !== drawnSamplingAreas) {
    setDrawnSamplingAreas(samplingAreas)
    setSamplingAreasLayerKey(key => key + 1)
  }

  const samplingAreasLayer = useMemo<GeoJSONType>(
    () => ({ type: 'FeatureCollection', features: samplingAreas ?? [] }),
    [samplingAreas],
  )

  // Hidden through style rather than by filtering the data: a remount would redraw the layer
  // above the overlays the user turned on. Leaflet merges setStyle options, so visible areas
  // must turn stroke/fill back on; with both off, SVG paths stop catching clicks and popups.
  const samplingAreaVisibilityStyle = useCallback(
    (feature: Feature) => {
      const isVisible = !hiddenSamplingAreaQUIds.has(feature.properties?.quantificationUnitId)
      return { stroke: isVisible, fill: isVisible }
    },
    [hiddenSamplingAreaQUIds],
  )

  const clustersGeoJSON = useMemo<GeoJSONType>(
    () => ({
      type: 'FeatureCollection',
      features: (clusters ?? []).flatMap(c => (c.toGeoJSON ? [c.toGeoJSON] : [])),
    }),
    [clusters],
  )

  const quForOverlay = useMemo(() => quantificationUnits ?? [], [quantificationUnits])

  const quNameById = useMemo<Record<string, string | null>>(
    () => Object.fromEntries(quForOverlay.map(qu => [qu.id, qu.name])),
    [quForOverlay],
  )

  const stratumPopupContent = useCallback(
    (feature: Feature) => buildStratumPopupContent(feature, quNameById),
    [quNameById],
  )

  return (
    <Box sx={{ position: 'relative', height: mapHeight, width: mapWidth }}>
      <Map
        scrollWheelZoom
        isLoading={isLoadingSamplingAreas}
        mapHeight={mapHeight}
        mapWidth={mapWidth}
        perimeter={currentFarm?.toGeoJSON}
      >
        {samplingAreas && samplingAreas.length > 0 && (
          <Map.Layer
            key={samplingAreasLayerKey}
            layer={samplingAreasLayer}
            layerStyles={samplingAreaVisibilityStyle}
            popupContent={stratumPopupContent}
          />
        )}

        <Map.LayersControl collapsed={false}>
          {/* Map drops child types it does not know; LayersControl renders any child inside the map context. */}
          <HiddenSamplingAreaPopupCloser hiddenQUIds={hiddenSamplingAreaQUIds} />
          <Map.BaseLayers />

          {clusters && clusters.length > 0 && (
            <Map.LayersControl.Pane name="Clúster biofísico">
              <Map.LayersControl.GeoJSONLayer data={clustersGeoJSON} />
            </Map.LayersControl.Pane>
          )}

          {quForOverlay.length > 0 && (
            <Map.LayersControl.Pane name="Unidades de Cuantificación">
              <QUOverlay quantificationUnits={quForOverlay} />
            </Map.LayersControl.Pane>
          )}

          <EligibilityLayers eligibilityAreas={eligibilityCheckAreas} />
        </Map.LayersControl>
      </Map>
    </Box>
  )
}
